import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Users, Plus, X, Loader2, CheckCircle, AlertTriangle, Clock, FolderKanban, Briefcase, TrendingUp, Edit2, Save } from "lucide-react";

const STATUS_CFG = {
  active:   { label: "Activo",    cls: "bg-green-100 text-green-700 border-green-200",  dot: "bg-green-500" },
  blocked:  { label: "Bloqueado", cls: "bg-red-100 text-red-700 border-red-200",        dot: "bg-red-500"   },
  pending:  { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200",  dot: "bg-amber-500" },
  inactive: { label: "Inactivo",  cls: "bg-gray-100 text-gray-500 border-gray-200",     dot: "bg-gray-400"  },
};

const ALLOC_COLOR = (pct) => {
  if (pct >= 60) return { bar: "bg-[#007934]", text: "text-[#007934]" };
  if (pct >= 30) return { bar: "bg-amber-500", text: "text-amber-600" };
  return { bar: "bg-gray-300", text: "text-gray-500" };
};

export default function Assignments() {
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects]       = useState([]);
  const [resources, setResources]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [editPct, setEditPct]         = useState(0);
  const [filterStatus, setFilterStatus] = useState("all");
  const [form, setForm]               = useState({
    project_id: "", project_title: "", resource_name: "",
    role: "", allocation_pct: 100, status: "active",
    modules: "", notes: ""
  });

  const load = useCallback(async () => {
    try {
      const [aRes, pRes, rRes] = await Promise.all([
        axios.get(`${API}/assignments`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/resources`),
      ]);
      setAssignments(aRes.data);
      setProjects(pRes.data);
      setResources(rRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        modules: form.modules ? form.modules.split(",").map(m => m.trim()).filter(Boolean) : [],
      };
      const { data } = await axios.post(`${API}/assignments`, payload);
      setAssignments(prev => [data, ...prev]);
      setShowModal(false);
      setForm({ project_id: "", project_title: "", resource_name: "", role: "", allocation_pct: 100, status: "active", modules: "", notes: "" });
    } catch (e) { alert("Error al crear asignación"); }
    finally { setSaving(false); }
  };

  const handleUpdatePct = async (aid) => {
    try {
      await axios.put(`${API}/assignments/${aid}`, { allocation_pct: Number(editPct) });
      setAssignments(prev => prev.map(a => a.id === aid ? { ...a, allocation_pct: Number(editPct) } : a));
      setEditingId(null);
    } catch (e) { alert("Error al actualizar"); }
  };

  const handleProjectChange = (pid) => {
    const proj = projects.find(p => p.id === pid);
    setForm(prev => ({ ...prev, project_id: pid, project_title: proj?.title || "" }));
  };

  const filtered = filterStatus === "all" ? assignments : assignments.filter(a => a.status === filterStatus);

  // KPIs
  const activeCount  = assignments.filter(a => a.status === "active").length;
  const blockedCount = assignments.filter(a => a.status === "blocked").length;
  const totalPctLoad = assignments.filter(a => a.status === "active").reduce((s, a) => s + (a.allocation_pct || 0), 0);
  const uniquePeople = [...new Set(assignments.map(a => a.resource_name).filter(Boolean))];

  return (
    <div className="space-y-5 w-full" data-testid="assignments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Asignaciones de Equipo</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gestión de dedicación y asignación de recursos internos a proyectos</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] flex items-center gap-2 shadow-sm transition-all active:scale-95"
          data-testid="new-assignment-btn">
          <Plus size={16} /> Nueva asignación
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="assignment-kpis">
        {[
          { label: "Personas asignadas", val: uniquePeople.length,  icon: Users,        cls: "text-[#007934]" },
          { label: "Asignaciones activas",val: activeCount,         icon: CheckCircle,  cls: "text-[#007934]" },
          { label: "Bloqueadas",          val: blockedCount,        icon: AlertTriangle,cls: blockedCount > 0 ? "text-red-600" : "text-gray-400" },
          { label: "Carga total activa",  val: `${totalPctLoad}%`,  icon: TrendingUp,   cls: "text-blue-600"  },
        ].map(k => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className={k.cls} />
              </div>
              <div>
                <p className={`text-2xl font-bold font-['Outfit'] ${k.cls}`}>{k.val}</p>
                <p className="text-xs text-gray-500">{k.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5" data-testid="assignment-filters">
        {[
          { key: "all",     label: "Todas" },
          { key: "active",  label: "Activas" },
          { key: "blocked", label: "Bloqueadas" },
          { key: "pending", label: "Pendientes" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterStatus(f.key)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-all font-medium ${filterStatus === f.key ? "bg-[#007934] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`filter-${f.key}`}>
            {f.label} {f.key === "all" ? `(${assignments.length})` : `(${assignments.filter(a => a.status === f.key).length})`}
          </button>
        ))}
      </div>

      {/* Assignments list */}
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 py-16 text-center">
          <Users size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Sin asignaciones</p>
          <p className="text-gray-400 text-sm mt-1">Crea una asignación para gestionar la dedicación del equipo</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="assignments-list">
          {filtered.map(a => {
            const cfg = STATUS_CFG[a.status] || STATUS_CFG.inactive;
            const alloc = ALLOC_COLOR(a.allocation_pct || 0);
            const modules = Array.isArray(a.modules) ? a.modules : [];
            return (
              <div key={a.id}
                className={`bg-white rounded-xl border transition-all hover:shadow-sm ${a.status === "blocked" ? "border-red-200" : "border-gray-100"}`}
                data-testid={`assignment-${a.id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full bg-[#007934]/10 flex items-center justify-center text-[#007934] text-xs font-bold flex-shrink-0">
                          {(a.resource_name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 leading-tight">{a.resource_name || a.task_title || "Sin nombre"}</h3>
                          <p className="text-xs text-gray-500">{a.role || a.company || ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1.5 flex-wrap">
                        <FolderKanban size={11} />
                        <span className="font-medium text-gray-700">{a.project_title || "Sin proyecto"}</span>
                        <span className={`ml-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.cls}`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1 align-middle`} />
                          {cfg.label}
                        </span>
                      </div>
                      {a.notes && <p className="text-xs text-gray-400 mt-1.5 line-clamp-1">{a.notes}</p>}
                      {modules.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {modules.slice(0, 5).map(m => (
                            <span key={m} className="text-[10px] px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-gray-600">{m}</span>
                          ))}
                          {modules.length > 5 && <span className="text-[10px] text-gray-400">+{modules.length - 5} más</span>}
                        </div>
                      )}
                    </div>

                    {/* Right: allocation */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0 min-w-[100px]">
                      {editingId === a.id ? (
                        <div className="flex items-center gap-1.5">
                          <input type="number" min="0" max="100" value={editPct}
                            onChange={e => setEditPct(e.target.value)}
                            className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-center focus:outline-none focus:border-[#007934]" />
                          <span className="text-xs text-gray-500">%</span>
                          <button onClick={() => handleUpdatePct(a.id)}
                            className="p-1.5 bg-[#007934] text-white rounded-lg hover:bg-[#006528]"
                            data-testid={`save-alloc-${a.id}`}>
                            <Save size={11} />
                          </button>
                          <button onClick={() => setEditingId(null)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <X size={11} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingId(a.id); setEditPct(a.allocation_pct || 0); }}
                          className="flex items-center gap-1 group"
                          data-testid={`edit-alloc-${a.id}`}>
                          <span className={`text-2xl font-bold font-['Outfit'] ${alloc.text}`}>{a.allocation_pct || 0}%</span>
                          <Edit2 size={11} className="text-gray-300 group-hover:text-gray-500 transition-colors mt-1" />
                        </button>
                      )}
                      <p className="text-[10px] text-gray-400">dedicación</p>
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${alloc.bar}`} style={{ width: `${Math.min(a.allocation_pct || 0, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" data-testid="create-assignment-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">Nueva Asignación</h2>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Proyecto *</label>
                  <select required value={form.project_id} onChange={e => handleProjectChange(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="assignment-project-select">
                    <option value="">Seleccionar proyecto...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Persona *</label>
                  <select required value={form.resource_name}
                    onChange={e => {
                      const r = resources.find(r => r.name === e.target.value);
                      setForm(prev => ({ ...prev, resource_name: e.target.value, role: r?.role || "" }));
                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="assignment-resource-select">
                    <option value="">Seleccionar persona...</option>
                    {resources.map(r => <option key={r.id} value={r.name}>{r.name} — {r.role}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Dedicación (%)</label>
                  <input type="number" min="0" max="100" required value={form.allocation_pct}
                    onChange={e => setForm(prev => ({ ...prev, allocation_pct: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="assignment-alloc-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Estado</label>
                  <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="assignment-status-select">
                    <option value="active">Activo</option>
                    <option value="pending">Pendiente</option>
                    <option value="blocked">Bloqueado</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Módulos (separados por comas)</label>
                  <input value={form.modules} onChange={e => setForm(prev => ({ ...prev, modules: e.target.value }))}
                    placeholder="ORA-KTLO, GDR, VSMSC-DEV"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="assignment-modules-input" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Notas</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
                    data-testid="assignment-notes-input" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="save-assignment-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null} Crear asignación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
